import { ServicePhotoViewer } from "./ServicePhotoViewer";
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Briefcase, Wrench, Gavel, Package } from "lucide-react";
import { api } from "../lib/api";
import { RfqCover } from "./RfqCover";
import { CoverArt } from "./cards";
import { ImageLightbox } from "./ImageLightbox";

const CAROUSEL_ICON: Record<string, any> = { job: Briefcase, service: Wrench, project: Gavel, item: Package };

function GalleryThumbnail({ src, index }: { src: string; index: number }) {
  const frame = useRef<HTMLSpanElement>(null);
  const [reached, setReached] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    // Native lazy loading can fetch an entire horizontal strip inside its preload distance.
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setReached(true); observer.disconnect(); }
    });
    if (frame.current) observer.observe(frame.current);
    return () => observer.disconnect();
  }, []);
  return <span ref={frame} className="block w-full h-full bg-navy-50">
    {reached && (failed ? <span className="text-xs text-mut">Photo unavailable</span> : <img src={src} alt={`Thumb ${index + 1}`} className="w-full h-full object-cover" onError={() => setFailed(true)} />)}
  </span>;
}

/**
 * v3.9.6 — post gallery carousel for detail pages. Fetches the post's images
 * and renders arrows, dots, a count pill, and a thumbnail strip.
 * v3.9.12 — when a post has no images (older posts), shows a branded placeholder
 * instead of rendering nothing, so every detail page has a hero image.
 */
export function ImageCarousel({ type, id, fallbackSrc, compact = false, narrowThumbnails = false, layout = "standard", mobileService = false, beuiViewer = false }: { type: string; id: string; fallbackSrc?: string | null; compact?: boolean; narrowThumbnails?: boolean; layout?: "standard" | "wide"; mobileService?: boolean; beuiViewer?: boolean }) {
  const [imgs, setImgs] = useState<{ id: string; url: string }[] | null>(null);
  const [cur, setCur] = useState(0);
  const [failed, setFailed] = useState<Record<string, boolean>>({});
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const fallbackImage = api.apiAssetUrl(fallbackSrc);

  useEffect(() => {
    let alive = true;
    api.postImages(type, id).then((r) => { if (alive) { setImgs(r); setCur(0); setFailed({}); } }).catch(() => { if (alive) setImgs([]); });
    return () => { alive = false; };
  }, [type, id]);

  // Empty/all-broken gallery → show the fallback image (e.g. an item's legacy
  // photo) if any; CoverArt handles its own load-error → branded placeholder.
  const Placeholder = () => (
    <div className={`card min-w-0 ${compact ? "mb-4 p-2" : layout === "wide" ? "p-1.5" : "p-3 mb-4"} ${mobileService ? "max-lg:mb-0 max-lg:p-0 max-lg:rounded-none max-lg:shadow-none max-lg:border-x-0" : ""}`}>
      <div className={`relative rounded-lg overflow-hidden ${compact ? "w-full h-[min(75vw,320px)] sm:h-[min(75vw,480px)]" : layout === "wide" ? "aspect-[16/6] sm:aspect-[16/5]" : "aspect-[4/3]"} ${mobileService ? "max-lg:aspect-[1.47] max-lg:h-auto max-lg:rounded-none" : ""}`}>
        {compact && fallbackImage && <img src={fallbackImage} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl" />}
        {fallbackImage
          ? <button type="button" onClick={() => setLightboxIndex(0)} aria-label="View photo full size" className="relative h-full w-full cursor-zoom-in">{type === "rfq" ? <RfqCover src={fallbackSrc} alt="RFQ cover" /> : <CoverArt src={fallbackSrc} alt="Photo" Icon={CAROUSEL_ICON[type] || Package} fit={compact ? "contain" : "cover"} />}</button>
          : type === "rfq" ? <RfqCover src={fallbackSrc} alt="RFQ cover" /> : <CoverArt src={fallbackSrc} alt="Photo" Icon={CAROUSEL_ICON[type] || Package} />}
      </div>
    </div>
  );

  const Viewer = beuiViewer ? ServicePhotoViewer : ImageLightbox;
  const fallbackViewer = lightboxIndex !== null && fallbackImage
    ? <Viewer images={[{ src: fallbackImage, alt: "Post photo" }]} initialIndex={0} onClose={() => setLightboxIndex(null)} />
    : null;

  if (imgs === null) return null; // still loading — avoid a placeholder flash
  // v3.9.17 — treat an all-broken gallery (bytes gone) the same as no photos.
  const usable = imgs.filter((im) => !failed[im.id]);
  if (imgs.length === 0 || usable.length === 0) return <>{Placeholder()}{fallbackViewer}</>;
  const n = imgs.length;
  const isProjectDemoImage = (im: { id: string }) => compact && type === "project" && id === "2storey-residential" && im.id === "pi-seed-project-2storey-residential-0";
  const src = (im: { id: string }) => isProjectDemoImage(im) ? "/hero/construction.jpg" : api.postImageSrc(type, id, im.id);
  const move = (d: number) => setCur((c) => (c + d + n) % n);

  return (
    <div className={`card min-w-0 ${compact ? "mb-4 p-2" : layout === "wide" ? "p-1.5" : "p-3 mb-4"} ${mobileService ? "max-lg:mb-0 max-lg:p-0 max-lg:rounded-none max-lg:shadow-none max-lg:border-x-0" : ""}`}>
      <div className={`relative rounded-lg overflow-hidden ${compact ? "bg-navy-50 w-full h-[min(75vw,320px)] sm:h-[min(75vw,480px)]" : layout === "wide" ? "bg-black aspect-[16/6] sm:aspect-[16/5]" : "bg-navy-50 aspect-[4/3]"} ${mobileService ? "max-lg:aspect-[1.47] max-lg:h-auto max-lg:rounded-none" : ""}`}>
        {(type === "project" || compact) && layout !== "wide" && <img src={src(imgs[cur])} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl" />}
        <button type="button" onClick={() => setLightboxIndex(cur)} aria-label={`View photo ${cur + 1} full size`} className="absolute inset-0 z-10 h-full w-full cursor-zoom-in">
          <img src={src(imgs[cur])} alt={`Photo ${cur + 1}`} className={`w-full h-full ${layout === "wide" ? "object-cover" : "object-contain"}`} onError={() => setFailed((f) => ({ ...f, [imgs[cur].id]: true }))} />
          {failed[imgs[cur].id] && <span role="status" className="absolute inset-0 grid place-items-center bg-navy-50 text-sm text-mut">Photo unavailable</span>}
        </button>
        {n > 1 && (
          <>
            <button onClick={() => move(-1)} aria-label="Previous" className={`absolute z-20 left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 grid place-items-center shadow ${mobileService ? "max-lg:hidden" : ""}`}><ChevronLeft className="w-5 h-5 text-navy" /></button>
            <button onClick={() => move(1)} aria-label="Next" className={`absolute z-20 right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 grid place-items-center shadow ${mobileService ? "max-lg:hidden" : ""}`}><ChevronRight className="w-5 h-5 text-navy" /></button>
            <div className={`absolute z-20 text-[11px] font-bold px-2 py-1 rounded-md ${compact ? "bottom-2 left-2 bg-white text-navy" : "top-2 right-2 bg-navy/75 text-white"} ${mobileService ? "max-lg:bottom-2 max-lg:left-auto max-lg:right-2 max-lg:bg-navy/75 max-lg:text-white" : ""}`}>{cur + 1} / {n}{isProjectDemoImage(imgs[cur]) ? " · illustrative photo" : ""}</div>
            {(!compact || mobileService) && <div className={`absolute z-20 bottom-2 inset-x-0 flex justify-center gap-1.5 ${mobileService ? "lg:hidden" : ""}`}>
              {imgs.map((_, i) => <button key={i} onClick={() => setCur(i)} className={`h-2 rounded-full ${i === cur ? "w-5 bg-white" : "w-2 bg-white/50"}`} aria-label={`Go to ${i + 1}`} />)}
            </div>}
          </>
        )}
      </div>
      {n > 1 && (
        <div className={`flex gap-2 mt-2 overflow-x-auto ${compact ? "pb-0.5" : ""} ${mobileService ? "max-lg:px-3 max-lg:pb-2 max-lg:mt-2" : ""}`}>
          {imgs.map((im, i) => (
            <button key={im.id} onClick={() => setCur(i)} aria-label={`Show photo ${i + 1}`} className={`${compact ? `h-[60px] ${narrowThumbnails ? "w-[100px]" : "w-[145px]"} shrink-0` : "w-16 aspect-[4/3] shrink-0"} ${mobileService ? "max-lg:h-12 max-lg:w-16" : ""} rounded overflow-hidden border-2 ${i === cur ? "border-blue-600" : "border-transparent"}`}>
              <GalleryThumbnail src={src(im)} index={i} />
            </button>
          ))}
        </div>
      )}
      {lightboxIndex !== null && <Viewer key={`${type}-${id}`} images={imgs.map((image, index) => ({ src: src(image), alt: `Photo ${index + 1}` }))} initialIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />}
    </div>
  );
}
